import { useEffect, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { meApi } from '../api/endpoints';
import type { StatutCode, UserDto } from '../api/types';
import { useAuth } from '../auth/useAuth';
import { useToast } from '../hooks/useToast';
import { AGENCES } from '../lib/commission';
import { fmtEur } from '../lib/format';
import { cropToSquareDataUrl } from '../lib/imageCrop';

interface OutletCtx {
  user?: UserDto;
  refreshUser: () => Promise<void>;
}

export function ProfilPage() {
  const { user, refreshUser } = useOutletContext<OutletCtx>();
  const auth = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [displayName, setDisplayName] = useState('');
  const [agence, setAgence] = useState('');
  const [statut, setStatut] = useState<StatutCode>('sal');
  const [target, setTarget] = useState('');
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    if (!user) return;
    setDisplayName(user.displayName);
    setAgence(user.agence);
    setStatut(user.statut);
    setTarget(user.targetCom != null ? String(user.targetCom) : '');
    setPhotoUrl(user.photoUrl ?? null);
  }, [user]);

  async function onSave() {
    setSaving(true);
    try {
      const t = parseFloat(target.replace(',', '.'));
      await meApi.update({
        displayName: displayName.trim(),
        agence,
        statut,
        targetCom: isNaN(t) ? null : t,
        photoUrl
      });
      await refreshUser();
      toast('Profil mis à jour ✨', 'success');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Erreur', 'error');
    } finally {
      setSaving(false);
    }
  }

  function triggerFilePicker() {
    fileInputRef.current?.click();
  }

  async function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Reset l'input pour pouvoir réuploader la même image après suppression
    e.target.value = '';
    if (!file) return;

    setUploadingPhoto(true);
    try {
      const dataUrl = await cropToSquareDataUrl(file);
      setPhotoUrl(dataUrl);
      toast('📷 Photo prête — pense à enregistrer', 'success');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Image illisible', 'error');
    } finally {
      setUploadingPhoto(false);
    }
  }

  function removePhoto() {
    setPhotoUrl(null);
    toast('🗑 Photo retirée — pense à enregistrer', 'success');
  }

  if (!user) return <div className="px-4 py-6 text-ink-sub">Chargement…</div>;

  const isAdmin = user.role === 'admin';
  const initials = user.displayName
    .split(' ').filter(Boolean).slice(0, 2)
    .map(s => s.charAt(0).toUpperCase()).join('') || '?';

  return (
    <div className="py-4 space-y-4">
      {/* ─── Avatar + identité ─── */}
      <section className="card text-center">
        <div className="inline-block relative">
          <button
            type="button"
            onClick={triggerFilePicker}
            disabled={uploadingPhoto}
            className="w-24 h-24 rounded-full border-2 border-border bg-surface-muted overflow-hidden flex items-center justify-center text-2xl font-demi text-ink-sub hover:border-teal/40 transition-colors relative"
            aria-label="Changer la photo de profil"
          >
            {photoUrl
              ? <img src={photoUrl} alt="" className="w-full h-full object-cover" />
              : <span>{initials}</span>}
            {/* Overlay caméra */}
            <span className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-teal text-white flex items-center justify-center text-sm border-2 border-white shadow-sm">
              📷
            </span>
          </button>
          {uploadingPhoto && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-full">
              <span className="text-xs text-ink-sub">…</span>
            </div>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={onFileChange}
          className="hidden"
        />

        <div className="mt-3 text-base font-demi text-ink">{user.displayName}</div>
        <div className="text-xs text-ink-sub mt-0.5">{user.email}</div>

        {isAdmin && (
          <span className="inline-block mt-2 text-[10px] uppercase tracking-wider bg-qts text-white px-2 py-0.5 rounded-full font-demi">
            Admin
          </span>
        )}

        {photoUrl && (
          <button
            type="button"
            onClick={removePhoto}
            className="mt-2 text-xs text-ink-sub hover:text-red-500 underline"
          >
            Retirer la photo
          </button>
        )}
      </section>

      {/* ─── Informations ─── */}
      <section className="card space-y-4">
        <div className="text-[11px] uppercase tracking-wider text-ink-sub font-demi">
          Informations
        </div>

        <Field label="Nom affiché">
          <input
            type="text"
            className="input"
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
          />
        </Field>

        <Field label="Agence">
          <select
            className="input"
            value={agence}
            onChange={e => setAgence(e.target.value)}
          >
            <option value="">— Sélectionner —</option>
            {AGENCES.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </Field>

        <div>
          <div className="block text-xs font-demi text-ink-sub mb-1.5">Statut</div>
          <div className="flex gap-2">
            <Pill selected={statut === 'sal'} onClick={() => setStatut('sal')}>
              Salarié VRP
            </Pill>
            <Pill selected={statut === 'mia'} onClick={() => setStatut('mia')}>
              CGP indépendant (MIA)
            </Pill>
          </div>
          <div className="text-[11px] text-ink-dim mt-1.5 font-light">
            ⚠️ Ce paramètre détermine le calcul de tes commissions.
          </div>
        </div>

        <Field label="Objectif mensuel de commission (optionnel)">
          <div className="flex items-center gap-2">
            <input
              type="number" min={0} step={100}
              className="input flex-1"
              placeholder="ex. 5000"
              value={target}
              onChange={e => setTarget(e.target.value)}
            />
            <span className="text-ink-sub text-sm">€</span>
          </div>
          {!isNaN(parseFloat(target)) && parseFloat(target) > 0 && (
            <div className="text-[11px] text-ink-dim mt-1 font-light">
              Objectif : {fmtEur(parseFloat(target.replace(',', '.')))}
            </div>
          )}
        </Field>

        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="w-full py-3 rounded-xl bg-teal text-white font-demi hover:bg-teal-dark transition-colors disabled:opacity-60"
        >
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </section>

      {/* ─── Admin ─── */}
      {isAdmin && (
        <section className="card">
          <div className="text-[11px] uppercase tracking-wider text-ink-sub font-demi mb-2">
            Administration
          </div>
          <button
            type="button"
            onClick={() => navigate('/admin')}
            className="w-full py-2.5 rounded-xl bg-surface-muted text-ink-sub hover:bg-border hover:text-ink text-sm transition-colors"
          >
            Espace admin
          </button>
        </section>
      )}

      {/* ─── Déconnexion ─── */}
      <section className="card">
        <button
          type="button"
          onClick={() => auth.logout()}
          className="w-full py-2.5 rounded-xl border border-red-200 text-red-600 hover:bg-red-50 text-sm transition-colors font-demi"
        >
          Se déconnecter
        </button>
      </section>

      <div className="text-center text-[11px] text-ink-dim pt-2 font-light">
        ComEasy v1.0 — Vendez. Déclarez. Brillez.
      </div>
    </div>
  );
}

// ─── Sous-composants ───────────────────────────────────────────

function Field({
  label, children
}: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-demi text-ink-sub mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function Pill({
  selected, onClick, children
}: { selected: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 px-3 py-2 rounded-full text-sm border transition-colors
        ${selected
          ? 'bg-teal text-white border-teal'
          : 'bg-white text-ink-sub border-border hover:bg-surface-muted'}`}
    >
      {children}
    </button>
  );
}
